import { getText, interpolate } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type CircumferenceTreePropertyDescriptor,
  type TreeDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { useIsRTL } from "@/hooks";

import { Input2, Slider } from "shared/components";
import { MeasurementEstimationAccordion } from "./MeasurementEstimationAccordion";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface CircumferenceFieldProps {
  hideMeasurementEstimationAccordion?: boolean;
  isMultiStem?: boolean | undefined | null;
  onChange: (treeDescriptor: TreeDescriptor) => void;
  treeDescriptor: TreeDescriptor;
}

export function CircumferenceField<
  T extends
    CircumferenceTreePropertyDescriptor = CircumferenceTreePropertyDescriptor,
>({
  hideMeasurementEstimationAccordion,
  isMultiStem,
  onChange,
  treeDescriptor,
}: CircumferenceFieldProps) {
  const isRTL = useIsRTL();

  const {
    isValid: isAboveGroundLevelValid,
    messages: aboveGroundLevelMessages,
  } = useTreeDialogValidator<T>({
    checker: (property) => !!property?.value,
    properties: treeDescriptor.properties,
    treePropertyType: TreePropertyType.circumference,
    id: "above-ground-level",
  });

  const { isValid: isGroundLevelValid, messages: groundLevelMessages } =
    useTreeDialogValidator<T>({
      checker: (property) => !!property?.groundLevelCircumference,
      properties: treeDescriptor.properties,
      treePropertyType: TreePropertyType.circumference,
      id: "ground-level",
    });

  const circumference = treeDescriptor.properties.get(
    TreePropertyType.circumference,
  ) as T | undefined;

  const handleChange = () => (updater: (draft: T) => void) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.circumference,
      updater,
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <FieldLabel isValid={isAboveGroundLevelValid}>
          {isMultiStem
            ? interpolate(
                "Addition of all tree stem circumferences at {{distanceAboveGround}} above ground level",
                {
                  distanceAboveGround: circumference?.distanceAboveGround,
                },
              )
            : interpolate(
                "Circumference at {{distanceAboveGround}} above ground level",
                { distanceAboveGround: circumference?.distanceAboveGround },
              )}
        </FieldLabel>

        <div className="flex items-center gap-2">
          <Slider
            defaultValue={[0.1]}
            max={3.5}
            min={0.1}
            step={0.05}
            onValueChange={(value) => {
              handleChange()((draft) => {
                draft.value = parseFloat(value[0].toString())
                  .toFixed(2)
                  .toString();
              });
            }}
            value={[circumference?.value ? parseFloat(circumference.value) : 0]}
          />

          <Input2
            className="text-bodymd placeholder:text-bodymd w-15 min-w-15 not-rtl:pl-1 not-rtl:pr-0 relative h-5 pr-0 rtl:pl-0 rtl:pr-1"
            {...(isRTL
              ? {
                  startClassName:
                    "border border-interactiveBorder bg-interfaceBgDarkPaper absolute left-[-1px] top-[-1px] bottom-[-1px] px-1 rounded-l-sm text-bodymd",
                  startContent: "m",
                }
              : {
                  endClassName:
                    "border border-interactiveBorder bg-interfaceBgDarkPaper absolute right-[-1px] top-[-1px] bottom-[-1px] px-1 rounded-r-sm text-bodymd",
                  endContent: "m",
                })}
            max={1.0}
            min={0.1}
            step={0.05}
            onChange={(event) => {
              handleChange()((draft) => {
                draft.value = event.target.value;
              });
            }}
            rounded="sm"
            type="number"
            value={circumference?.value || ""}
          />
        </div>

        <FieldValidationMessages messages={aboveGroundLevelMessages} />
      </div>

      <div className="flex flex-col gap-2">
        <FieldLabel isValid={isGroundLevelValid}>
          {isMultiStem
            ? getText(
                "Addition of all tree stem circumferences at ground level",
              )
            : getText("Circumference at ground level")}
        </FieldLabel>

        <div className="flex items-center gap-2">
          <Slider
            defaultValue={[0.1]}
            max={3.5}
            min={0.1}
            step={0.05}
            onValueChange={(value) => {
              handleChange()((draft) => {
                draft.groundLevelCircumference = parseFloat(value[0].toString())
                  .toFixed(2)
                  .toString();
              });
            }}
            value={[
              circumference?.groundLevelCircumference
                ? parseFloat(circumference.groundLevelCircumference)
                : 0,
            ]}
          />

          <Input2
            className="text-bodymd placeholder:text-bodymd w-15 min-w-15 not-rtl:pl-1 not-rtl:pr-0 relative h-5 pr-0 rtl:pl-0 rtl:pr-1"
            {...(isRTL
              ? {
                  startClassName:
                    "border border-interactiveBorder bg-interfaceBgDarkPaper absolute left-[-1px] top-[-1px] bottom-[-1px] px-1 rounded-l-sm text-bodymd",
                  startContent: "m",
                }
              : {
                  endClassName:
                    "border border-interactiveBorder bg-interfaceBgDarkPaper absolute right-[-1px] top-[-1px] bottom-[-1px] px-1 rounded-r-sm text-bodymd",
                  endContent: "m",
                })}
            max={1.0}
            min={0.1}
            step={0.05}
            onChange={(event) => {
              handleChange()((draft) => {
                draft.groundLevelCircumference = event.target.value;
              });
            }}
            rounded="sm"
            type="number"
            value={circumference?.groundLevelCircumference || ""}
          />
        </div>

        <FieldValidationMessages messages={groundLevelMessages} />
      </div>

      {!hideMeasurementEstimationAccordion && (
        <MeasurementEstimationAccordion
          onClickSmall={() => {
            handleChange()((draft) => {
              draft.value = isMultiStem ? "0.90" : "0.70";
              draft.groundLevelCircumference = isMultiStem ? "0.70" : "0.70";
            });
          }}
          onClickMedium={() => {
            handleChange()((draft) => {
              draft.value = isMultiStem ? "1.70" : "1.40";
              draft.groundLevelCircumference = isMultiStem ? "1.60" : "1.60";
            });
          }}
          onClickLarge={() => {
            handleChange()((draft) => {
              draft.value = isMultiStem ? "2.90" : "2.00";
              draft.groundLevelCircumference = isMultiStem ? "2.40" : "2.40";
            });
          }}
        />
      )}
    </div>
  );
}
